import { scoreToStars } from '../src/backend/typesense/scoreToStars'
import type { BlogPostDetailResult } from '../src/data/BlogPostFragment'
import type { ProductDetailResult } from '../src/data/ProductFragment'
import type { ProductReviewResult } from '../src/data/ProductReviewFragment'

const baseUrl = process.env.NEXT_PUBLIC_BASE_URL

export function useStructuredDataProduct(
	product: ProductDetailResult,
	rating: number | undefined,
	reviews: ProductReviewResult[],
) {
	const name = product.productLine
		? `${product.brand?.name} ${product.productLine?.name} ${product.name}`
		: `${product.brand?.name} ${product.name}`

	const image = product.heurekaImage?.url ?? product.image?.url
	const url = `${baseUrl}p/${product.slug}`
	const description = product.detailedComposition
	const brand = {
		'@type': 'Brand',
		name: product.brand?.name,
		url: `${baseUrl}z/${product.brand?.slug}`,
		image: product.brand?.logo?.url,
		description: product.brand?.lead,
	}

	if (rating && rating > 0) {
		const data = {
			'@context': 'http://schema.org/',
			'@type': 'Product',
			name,
			image,
			description,
			url,
			brand,
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: rating,
				bestRating: 5,
				reviewCount: reviews.length || 1,
			},
			review: reviews.map((review) => {
				return {
					'@type': 'Review',
					reviewRating: {
						'@type': 'Rating',
						ratingValue: review.score?.starsAmount,
						bestRating: 5,
					},
					reviewBody: review.text,
					author: {
						'@type': 'Person',
						name: review.fullName,
					},
				}
			}),
		}

		const results = JSON.stringify(data)
		return results
	}

	const data = {
		'@context': 'http://schema.org/',
		'@type': 'Product',
		name,
		image,
		description,
		url,
		brand,
		aggregateRating: {
			'@type': 'AggregateRating',
			bestRating: 5,
			ratingValue: scoreToStars(product.score),
			reviewCount: reviews.length || 1,
		},
	}

	const results = JSON.stringify(data)
	return results
}

export function useStructuredDataBlogPost(blogPost: BlogPostDetailResult | undefined) {
	const data = {
		'@context': 'http://schema.org/',
		'@type': 'Article',
		headline: blogPost?.title,
		image: blogPost?.image?.url,
		description: blogPost?.lead,
		datePublished: blogPost?.publishedAt,
		dateModified: blogPost?.createdAt,
		url: `${baseUrl}c/${blogPost?.slug}`,
		author: {
			'@type': 'Person',
			name: blogPost?.author,
		},
		publisher: {
			'@type': 'Organization',
			name: 'Granlupo',
		},
		logo: {
			'@type': 'ImageObject',
			url: 'https://granlupo.cz/images/logo.jpg',
		},
	}

	const results = JSON.stringify(data)
	return results
}

export function useStructuredDataBreadcrumbs(menuItems: { name: string; link: string }[]) {
	const data = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		'@id': baseUrl,
		name: 'Granlupo menu',
		itemListElement: menuItems.map((item, index) => {
			return {
				'@type': 'ListItem',
				position: index + 1,
				name: item.name,
				item: `${baseUrl?.slice(0, -1)}${item.link}`,
			}
		}),
	}

	const results = JSON.stringify(data)
	return results
}
